import { pluralize } from '@fourthwall/utils/dist/string'
import isUndefined from 'lodash-es/isUndefined'
import type { ChangeEvent, FC, FocusEvent } from 'react'
import React, { useLayoutEffect, useState } from 'react'
import { withFormikSupport } from '../../hocs/withFormikSupport/withFormikSupport'
import { InputUI } from '../../ui'
import * as S from './Textarea.styled'
import type { Props } from './types'

const TextareaComponent: FC<Props> = ({
  placeholder,
  disabled,
  focused,
  tabIndex,
  fullWidth,
  label,
  name,
  value = '',
  rows = 2,
  resize = false,
  invalid = false,
  required,
  maxLength,
  info,
  error,
  displayError = true,
  submitCount,
  suggestion,
  onChange,
  onValueChange,
  onBlur,
  onFocus,
}) => {
  const [isFocused, setIsFocused] = useState(false)

  useLayoutEffect(() => {
    if (!isUndefined(focused)) {
      setIsFocused(focused)
    }
  }, [focused])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e)
    }

    if (onValueChange) {
      onValueChange(e.target.value)
    }
  }

  const handleFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true)

    if (onFocus) {
      onFocus(e)
    }
  }

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false)

    if (onBlur) {
      onBlur(e)
    }
  }

  const getTextareaId = () => {
    if (!name) {
      return undefined
    }

    return `${name}__textarea`
  }

  const charactersLeft = (maxLength || Infinity) - (value?.length || 0)

  return (
    <S.Container fullWidth={fullWidth}>
      <InputUI.Box
        $invalid={invalid}
        $fullWidth={fullWidth}
        $fullHeight
        $disabled={disabled}
        $focused={isFocused}
      >
        <S.Textarea
          rows={rows}
          $resize={resize}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          name={name}
          tabIndex={tabIndex}
          id={getTextareaId()}
          value={value}
          aria-invalid={invalid ? 'true' : 'false'}
          maxLength={maxLength}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          data-testid="Textarea"
        />
        <S.Label htmlFor={getTextareaId()}>{label}</S.Label>
        {(maxLength || info) && (
          <InputUI.Info>
            {info || `${charactersLeft} ${pluralize(charactersLeft, 'character')} left`}
          </InputUI.Info>
        )}
      </InputUI.Box>
      {(!displayError || !error) && suggestion && (
        <InputUI.Suggestion>{suggestion}</InputUI.Suggestion>
      )}
      {displayError && !!error && (
        <InputUI.Error errorTimestampKey={submitCount}>{error}</InputUI.Error>
      )}
    </S.Container>
  )
}

export const Textarea = withFormikSupport(TextareaComponent)
